/**
 * Admin → Social Media → Content Generation
 * Implements Tier 1 (Interface) of the 3-tier Instagram weekly content pipeline.
 * Tier 2 (Orchestration) lives server-side in /app/backend/social_routes.py
 * so the LLM API key never touches the browser.
 *
 * UI = Config panel  +  Generate button  +  Review grid (7 day cards).
 * Each card is independently approvable / rejectable / image-generatable.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Sparkles, Image as ImageIcon, Loader2,
  Settings, Calendar, Save,
  Wand2, Check, X, RefreshCw, Plus, Clock
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function AdminSocialMedia() {
  const { toast } = useToast();
  const [tab, setTab] = useState('generator'); // generator | history | config
  const [config, setConfig] = useState(null);
  const [week, setWeek] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [progressStage, setProgressStage] = useState('');
  const [progressPct, setProgressPct] = useState(0);
  // Lifted so "+ Create new week" can reset it
  const [theme, setTheme] = useState('');

  const authH = { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } };

  useEffect(() => {
    (async () => {
      try {
        const [cfg, lst] = await Promise.all([
          axios.get(`${API_URL}/api/admin/social/config`, authH),
          axios.get(`${API_URL}/api/admin/social/weeks?limit=20`, authH),
        ]);
        setConfig(cfg.data);
        setWeeks(lst.data.weeks || []);
        if (lst.data.weeks?.length) setWeek(lst.data.weeks[0]);
      } catch (e) { /* will show empty state */ }
    })();
    // eslint-disable-next-line
  }, []);

  const generate = async (weeklyOverrides = {}) => {
    setGenerating(true);
    setProgressStage('Starting…');
    setProgressPct(2);
    try {
      // 1. Kick off the async job
      const { data: job } = await axios.post(
        `${API_URL}/api/admin/social/generate-week`,
        { brand: config, weekly: weeklyOverrides },
        authH,
      );

      // 2. Poll the job until done/error — tolerate transient ingress hiccups
      const pollStart = Date.now();
      let consecutiveFails = 0;
      while (true) {
        await new Promise(r => setTimeout(r, 3000));
        if (Date.now() - pollStart > 300_000) {  // 5-min absolute ceiling
          throw new Error('Job took longer than 5 minutes — please check Past Weeks tab.');
        }
        let jobNow;
        try {
          const r = await axios.get(`${API_URL}/api/admin/social/jobs/${job.id}`, authH);
          jobNow = r.data;
          consecutiveFails = 0;
        } catch (pollErr) {
          consecutiveFails += 1;
          if (consecutiveFails >= 5) throw pollErr;
          continue;
        }
        setProgressStage(jobNow.stage || '');
        setProgressPct(jobNow.progress || 0);
        if (jobNow.status === 'done') {
          setWeek(jobNow.week);
          try {
            const refreshed = await axios.get(`${API_URL}/api/admin/social/weeks?limit=20`, authH);
            setWeeks(refreshed.data.weeks || []);
          } catch (_) { /* listing failed — non-blocking */ }
          toast({ title: 'Week generated', description: `7 posts ready · theme: "${jobNow.week.week_theme}"` });
          break;
        }
        if (jobNow.status === 'error') {
          throw new Error(jobNow.error || 'Generation failed');
        }
      }
    } catch (e) {
      toast({
        title: 'Generation failed',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
      setProgressStage('');
      setProgressPct(0);
    }
  };

  if (!config) return <div className="p-8 text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="social-root">
      {/* Sub-tabs + Create button */}
      <div className="flex items-center justify-between border-b">
        <div className="flex gap-2">
          {[
            { id: 'generator', label: 'Content Generation', icon: Sparkles },
            { id: 'history',   label: 'Past Weeks',          icon: Calendar },
            { id: 'config',    label: 'Brand Config',        icon: Settings },
          ].map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`social-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 text-sm font-medium ${
                  active ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
        <Button
          onClick={() => {
            // Start fresh: clear typed theme and the visible banner. Don't auto-generate —
            // the user will configure the variables (including the theme) and then hit
            // "Generate the week" inside the form to actually run it.
            setTheme('');
            setWeek(null);
            setTab('generator');
          }}
          disabled={generating}
          size="sm"
          className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-md mb-2"
          data-testid="create-new-week-btn"
          title="Clear the form and start a fresh week"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Create new week
        </Button>
      </div>

      {tab === 'generator' && (
        <GeneratorTab
          config={config} week={week} setWeek={setWeek}
          generate={generate} generating={generating}
          progressStage={progressStage} progressPct={progressPct}
          weeks={weeks}
          theme={theme} setTheme={setTheme}
        />
      )}
      {tab === 'history' && <HistoryTab weeks={weeks} onPick={setWeek} onSwitchTab={() => setTab('generator')} />}
      {tab === 'config' && <ConfigTab config={config} setConfig={setConfig} authH={authH} />}
    </div>
  );
}

// ===================== Generator Tab =====================

function GeneratorTab({ config, week, setWeek, generate, generating, progressStage, progressPct, weeks, theme, setTheme }) {
  const { toast } = useToast();
  const [pillarMix, setPillarMix] = useState({
    education: 2, behind_the_scenes: 1, testimonial: 1,
    myth_buster: 1, tip_of_the_day: 1, engagement_question: 1,
  });
  const [contentMix, setContentMix] = useState({
    single_post: 3, carousel: 2, reel: 2,
  });

  const onGenerate = () => generate({
    week_theme: theme.trim() || null,
    pillar_mix: pillarMix,
    content_type_mix: contentMix,
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Weekly variables panel */}
      <div className="lg:col-span-4 space-y-4">
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-pink-500" /> This week's variables
            </CardTitle>
            <CardDescription className="text-xs">
              Brand voice stays locked (edit it in <strong>Brand Config</strong>). Tweak per-week below.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs">Week theme <span className="text-gray-400">(leave empty to let AI choose)</span></Label>
              <Input
                placeholder="e.g. Reclaiming screen-time"
                value={theme}
                onChange={e => setTheme(e.target.value)}
                data-testid="theme-input"
              />
            </div>

            <MixControl
              label="Pillar mix (must total 7)"
              total={7}
              value={pillarMix}
              setValue={setPillarMix}
              testIdPrefix="pillar"
            />

            <MixControl
              label="Content type mix (must total 7)"
              total={7}
              value={contentMix}
              setValue={setContentMix}
              testIdPrefix="ctype"
              labels={{
                single_post: 'Single post',
                carousel: 'Carousel',
                reel: 'Reel',
                story: 'Story',
              }}
            />

            <Button
              onClick={onGenerate}
              disabled={generating}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white h-11"
              data-testid="generate-week-btn"
            >
              {generating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
                : <><Sparkles className="w-4 h-4 mr-2" /> Generate the week</>}
            </Button>
            {generating && (
              <div className="space-y-1">
                <div className="h-1.5 bg-pink-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-pink-500 to-purple-600 transition-all duration-500"
                    style={{ width: `${Math.max(progressPct || 5, 5)}%` }} />
                </div>
                <p className="text-xs text-gray-600">{progressStage || 'Starting…'}</p>
              </div>
            )}
            <p className="text-xs text-gray-400">
              First call plans the theme (~5 sec). Then 7 posts run in parallel (~60-90 sec total).
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-pink-50 to-purple-50">
          <CardContent className="p-4 text-xs text-gray-600 space-y-2">
            <p className="font-semibold text-gray-700">🧠 How it works</p>
            <ol className="list-decimal list-inside space-y-1">
              <li><strong>Stage 1:</strong> Claude Sonnet 4.5 reads your config + theme → returns a 7-day plan</li>
              <li><strong>Stage 2:</strong> 7 parallel Claude calls — one per day. One failed post doesn't sink the rest.</li>
              <li><strong>Stage 3:</strong> Click "Generate image" on any card → Gemini Nano Banana creates the hero visual</li>
              <li><strong>Reject</strong> any card → only that day re-runs with your feedback. Theme stays locked.</li>
            </ol>
          </CardContent>
        </Card>
      </div>

      {/* Review grid */}
      <div className="lg:col-span-8">
        {!week ? (
          <Card className="border-2 border-dashed border-gray-200 bg-white/50">
            <CardContent className="py-16 text-center">
              <Sparkles className="w-12 h-12 mx-auto text-pink-300 mb-3" />
              <p className="text-gray-500">No week generated yet.</p>
              <p className="text-xs text-gray-400 mt-1">Tweak the variables on the left and click "Generate the week".</p>
              {weeks.length > 0 && (
                <p className="text-xs text-gray-500 mt-3">
                  Or open a past week from the <strong>Past Weeks</strong> tab.
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <ReviewGrid week={week} setWeek={setWeek} />
        )}
      </div>
    </div>
  );
}

function MixControl({ label, total, value, setValue, testIdPrefix, labels = {} }) {
  const sum = Object.values(value).reduce((a, b) => a + Number(b || 0), 0);
  const ok = sum === total;
  return (
    <div>
      <Label className="text-xs flex items-center justify-between">
        <span>{label}</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ${ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
          {sum}/{total}
        </span>
      </Label>
      <div className="grid grid-cols-2 gap-2 mt-1">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <Input
              type="number" min="0" max="7"
              value={v}
              onChange={e => setValue({ ...value, [k]: Math.max(0, Math.min(7, parseInt(e.target.value) || 0)) })}
              className="h-8 w-14 text-xs"
              data-testid={`${testIdPrefix}-${k}`}
            />
            <span className="text-xs text-gray-600 truncate">{labels[k] || k.replace(/_/g, ' ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===================== Review Grid (minimal preview) =====================

function ReviewGrid({ week, setWeek }) {
  const { toast } = useToast();
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStage, setBulkStage] = useState('');
  const [bulkPct, setBulkPct] = useState(0);

  const authH = { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } };
  const totalCards = week.cards.length;
  const withImage = week.cards.filter(c => c.image_url).length;

  const generateAllMedia = async () => {
    setBulkBusy(true);
    setBulkStage('Starting…');
    setBulkPct(2);
    try {
      const { data: job } = await axios.post(
        `${API_URL}/api/admin/social/generate-all-images`,
        { week_id: week.id, overwrite: true },
        authH,
      );
      const start = Date.now();
      let consecutiveFails = 0;
      while (true) {
        await new Promise(r => setTimeout(r, 2500));
        if (Date.now() - start > 240_000) throw new Error('Job exceeded 4 minutes');
        let now;
        try {
          const r = await axios.get(`${API_URL}/api/admin/social/jobs/${job.id}`, authH);
          now = r.data;
          consecutiveFails = 0;
        } catch (pollErr) {
          // Transient ingress/network errors during the long run shouldn't kill the flow.
          consecutiveFails += 1;
          if (consecutiveFails >= 5) throw pollErr;
          continue;
        }
        setBulkStage(now.stage || '');
        setBulkPct(now.progress || 0);
        if (now.status === 'done' || now.status === 'error') {
          // Single heavy refresh at the end — pull the full week (with image URLs) once
          try {
            const { data: fresh } = await axios.get(`${API_URL}/api/admin/social/weeks/${week.id}`, authH);
            setWeek(fresh);
          } catch (refreshErr) {
            // Even if the heavy fetch fails (rare ingress 502 on 7MB), the job itself completed.
            // Surface the success and let the user reopen the week from Past Weeks if needed.
          }
          if (now.status === 'error') throw new Error(now.error || 'Job failed');
          const errs = (now.errors || []).length;
          toast({
            title: errs ? `${(now.total || 0) - errs}/${now.total} media generated` : 'All 7 media generated',
            description: errs ? `${errs} day(s) failed — click again to retry.` : undefined,
            variant: errs ? 'destructive' : 'default',
          });
          break;
        }
      }
    } catch (e) {
      toast({ title: 'Generation failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBulkBusy(false);
      setBulkStage('');
      setBulkPct(0);
    }
  };

  return (
    <div className="space-y-4">
      {/* Theme + single action button */}
      <Card className="border-0 shadow-md bg-gradient-to-r from-pink-500 to-purple-600 text-white">
        <CardContent className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest opacity-80">Week theme</p>
            <p className="text-xl font-bold mt-1 truncate">{week.week_theme}</p>
          </div>
          <div className="w-full md:w-auto md:min-w-[280px] flex flex-col gap-2">
            <Button
              onClick={generateAllMedia}
              disabled={bulkBusy}
              className="bg-white text-purple-700 hover:bg-purple-50 font-semibold h-11"
              data-testid="generate-all-media-btn"
            >
              {bulkBusy
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
                : <><Sparkles className="w-4 h-4 mr-2" /> Generate all 7 media</>}
            </Button>
            {bulkBusy ? (
              <div>
                <div className="h-1.5 bg-white/30 rounded-full overflow-hidden">
                  <div className="h-full bg-white transition-all duration-500" style={{ width: `${Math.max(bulkPct, 5)}%` }} />
                </div>
                <p className="text-[11px] mt-1 opacity-90">{bulkStage}</p>
              </div>
            ) : (
              <p className="text-[11px] opacity-80 text-center md:text-right">
                {withImage}/{totalCards} media ready
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 7 minimal preview cards: day + media + caption + approve/reject */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {week.cards.map(card => (
          <MediaCard
            key={card.day_index}
            card={card}
            weekId={week.id}
            onUpdate={(newCard) => {
              const updated = { ...week };
              updated.cards = updated.cards.map(c => c.day_index === newCard.day_index ? newCard : c);
              setWeek(updated);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function MediaCard({ card, weekId, onUpdate }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState('');
  const [feedback, setFeedback] = useState('');
  const authH = { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } };
  const post = card.post || {};
  const isApproved = card.status === 'approved';
  const isRejected = card.status === 'rejected';

  const setStatus = async (status) => {
    setBusy(status);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/social/approve-day`,
        { week_id: weekId, day_index: card.day_index, status },
        authH,
      );
      onUpdate(data.card);
      toast({ title: status === 'approved' ? 'Approved' : status === 'rejected' ? 'Marked not approved' : 'Reset to draft' });
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy('');
    }
  };

  // Regenerate the rejected card: new copy (Stage 2 Claude) + a fresh image (Nano Banana).
  // Feedback note is fed back into the Claude prompt so the next pass actually improves.
  const regenerateBetter = async () => {
    setBusy('regen');
    try {
      // 1. Rewrite the post with feedback (theme stays locked)
      const { data: regen } = await axios.post(
        `${API_URL}/api/admin/social/regenerate-day`,
        { week_id: weekId, day_index: card.day_index, rejection_note: feedback || null },
        { ...authH, timeout: 60000 },
      );
      onUpdate(regen.card);
      // 2. Generate a fresh image for the new copy
      const { data: img } = await axios.post(
        `${API_URL}/api/admin/social/generate-image`,
        { week_id: weekId, day_index: card.day_index },
        { ...authH, timeout: 120000 },
      );
      onUpdate({ ...regen.card, image_url: img.image_url });
      setFeedback('');
      toast({ title: 'Regenerated', description: 'Fresh copy + new image for this day' });
    } catch (e) {
      toast({
        title: 'Regenerate failed',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  // Border tints the whole card so approval state is visible at a glance
  const borderClass = isApproved
    ? 'ring-2 ring-emerald-400'
    : isRejected
      ? 'ring-2 ring-red-300'
      : '';

  return (
    <Card className={`border-0 shadow-md overflow-hidden flex flex-col ${borderClass}`} data-testid={`media-card-${card.day_index}`}>
      {/* Day label + status badge */}
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-widest text-pink-600" data-testid={`day-label-${card.day_index}`}>
          {DAYS_FULL[card.day_index]}
        </p>
        {isApproved && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold" data-testid={`status-${card.day_index}`}>
            Approved
          </span>
        )}
        {isRejected && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold" data-testid={`status-${card.day_index}`}>
            Not approved
          </span>
        )}
      </div>

      {/* Media */}
      <div className="aspect-square w-full bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center" data-testid={`media-${card.day_index}`}>
        {busy === 'regen' ? (
          <div className="text-center text-xs text-gray-500">
            <Loader2 className="w-8 h-8 mx-auto mb-2 text-pink-400 animate-spin" />
            <p>Regenerating with your feedback…</p>
            <p className="opacity-60 mt-0.5">~30–60 sec</p>
          </div>
        ) : card.image_url ? (
          <img src={card.image_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="text-center text-xs text-gray-400">
            <ImageIcon className="w-8 h-8 mx-auto mb-2 text-pink-200" />
            <p>Media not generated yet</p>
          </div>
        )}
      </div>

      {/* Caption */}
      <div className="p-4 flex-1" data-testid={`caption-${card.day_index}`}>
        {post.caption ? (
          <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{post.caption}</p>
        ) : card.status === 'error' ? (
          <p className="text-sm text-red-600">⚠ {card.error || 'Generation error'}</p>
        ) : (
          <p className="text-sm text-gray-400 italic">No caption yet</p>
        )}
      </div>

      {/* When rejected: feedback input + regenerate button */}
      {isRejected && (
        <div className="px-4 pb-3 space-y-2 border-t pt-3 bg-red-50/40" data-testid={`regen-panel-${card.day_index}`}>
          <Input
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            placeholder="What should change? e.g. warmer tone, less corporate, focus on the parent"
            className="text-xs h-9"
            disabled={!!busy}
            data-testid={`regen-feedback-${card.day_index}`}
          />
          <Button
            size="sm"
            onClick={regenerateBetter}
            disabled={!!busy}
            className="w-full h-9 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
            data-testid={`regen-${card.day_index}`}
          >
            {busy === 'regen'
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Regenerating…</>
              : <><RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate with feedback</>}
          </Button>
        </div>
      )}

      {/* Approve / Not approve actions */}
      {post.caption && (
        <div className="px-4 pb-4 flex gap-2 border-t pt-3" data-testid={`actions-${card.day_index}`}>
          <Button
            size="sm"
            onClick={() => setStatus(isApproved ? 'draft' : 'approved')}
            disabled={!!busy}
            className={`flex-1 h-9 ${isApproved ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-emerald-500 hover:bg-emerald-600'} text-white`}
            data-testid={`approve-${card.day_index}`}
          >
            {busy === 'approved' || busy === 'draft'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <><Check className="w-4 h-4 mr-1.5" /> {isApproved ? 'Approved' : 'Approve'}</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setStatus(isRejected ? 'draft' : 'rejected')}
            disabled={!!busy}
            className={`flex-1 h-9 ${isRejected ? 'border-red-400 text-red-700 bg-red-50' : 'border-red-200 text-red-600 hover:bg-red-50'}`}
            data-testid={`reject-${card.day_index}`}
          >
            {busy === 'rejected'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <><X className="w-4 h-4 mr-1.5" /> {isRejected ? 'Not approved' : 'Not approve'}</>}
          </Button>
        </div>
      )}
    </Card>
  );
}


// ===================== History Tab =====================

function HistoryTab({ weeks, onPick, onSwitchTab }) {
  if (weeks.length === 0) {
    return <p className="text-center text-gray-400 py-12">No weeks generated yet. Switch to the Content Generation tab and click Generate.</p>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {weeks.map(w => {
        const approved = w.cards.filter(c => c.status === 'approved').length;
        const errors = w.cards.filter(c => c.status === 'error').length;
        return (
          <Card key={w.id} className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => { onPick(w); onSwitchTab(); }} data-testid={`past-week-${w.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700 bg-pink-50 px-2 py-1 rounded-full" data-testid={`past-week-timestamp-${w.id}`}>
                  <Clock className="w-3 h-3 text-pink-500" />
                  {new Date(w.created_at).toLocaleString(undefined, {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </div>
              </div>
              <p className="font-bold text-base mt-1">{w.week_theme}</p>
              <p className="text-xs text-gray-500 line-clamp-2 mt-1">{w.theme_rationale}</p>
              <div className="flex items-center gap-3 mt-3 text-xs">
                <span className="text-emerald-600">{approved}/7 approved</span>
                {errors > 0 && <span className="text-red-500">{errors} errors</span>}
              </div>
              {w.updated_at && w.updated_at !== w.created_at && (
                <p className="text-[10px] text-gray-400 mt-2">
                  Last edited {new Date(w.updated_at).toLocaleString(undefined, {
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ===================== Config Tab =====================

function ConfigTab({ config, setConfig, authH }) {
  const { toast } = useToast();
  const [form, setForm] = useState(config);
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(config), [form, config]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.updated_at;
      await axios.put(`${API_URL}/api/admin/social/config`, payload, authH);
      setConfig(form);
      toast({ title: 'Saved', description: 'Brand config updated. New generations will use it.' });
    } catch (e) { toast({ title: 'Failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const arrField = (key, label, placeholder) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        value={(form[key] || []).join(', ')}
        onChange={e => setForm({ ...form, [key]: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
        placeholder={placeholder}
        data-testid={`config-${key}`}
      />
      <p className="text-[10px] text-gray-400 mt-0.5">Comma-separated</p>
    </div>
  );

  return (
    <Card className="border-0 shadow-md max-w-3xl">
      <CardHeader>
        <CardTitle className="text-base">Brand identity (immutable core)</CardTitle>
        <CardDescription className="text-xs">Locked across every week. Changing here affects all future generations.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Brand name</Label>
            <Input value={form.brand_name || ''} onChange={e => setForm({ ...form, brand_name: e.target.value })} data-testid="config-brand_name" />
          </div>
          <div>
            <Label className="text-xs">Tagline</Label>
            <Input value={form.tagline || ''} onChange={e => setForm({ ...form, tagline: e.target.value })} data-testid="config-tagline" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Voice</Label>
          <Input value={form.voice || ''} onChange={e => setForm({ ...form, voice: e.target.value })} data-testid="config-voice" />
        </div>
        <div>
          <Label className="text-xs">Audience</Label>
          <Input value={form.audience || ''} onChange={e => setForm({ ...form, audience: e.target.value })} data-testid="config-audience" />
        </div>
        <div>
          <Label className="text-xs">Default CTA</Label>
          <Input value={form.cta_default || ''} onChange={e => setForm({ ...form, cta_default: e.target.value })} data-testid="config-cta" />
        </div>
        {arrField('products', 'Products / services', 'AI observation, parent reports')}
        {arrField('do_say', 'DO say', 'mindful, evidence-led')}
        {arrField('dont_say', "DON'T say", 'diagnose, broken')}

        <Button onClick={save} disabled={!dirty || saving} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="config-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </CardContent>
    </Card>
  );
}
